import React from 'react';

interface Point {
  x: number;
  y: number;
  lat: number;
  lng: number;
}

interface PolygonDrawingProps {
  isDrawing: boolean;
  polygonPoints: Point[];
  currentMousePoint: Point | null;
}

export const PolygonDrawing: React.FC<PolygonDrawingProps> = ({
  isDrawing,
  polygonPoints,
  currentMousePoint
}) => {
  if (!isDrawing || polygonPoints.length === 0) return null;

  // Create SVG path for polygon
  const getSvgPath = () => {
    if (polygonPoints.length === 0) return '';

    let path = `M ${polygonPoints[0].x} ${polygonPoints[0].y}`;

    for (let i = 1; i < polygonPoints.length; i++) {
      path += ` L ${polygonPoints[i].x} ${polygonPoints[i].y}`;
    }

    // Add line to current mouse position if available
    if (currentMousePoint) {
      path += ` L ${currentMousePoint.x} ${currentMousePoint.y}`;
    }

    return path;
  };

  // Create SVG polygon for filled area (closed shape)
  const getPolygonPoints = () => {
    const points = polygonPoints.map(p => `${p.x},${p.y}`).join(' ');
    if (currentMousePoint) {
      return points + ` ${currentMousePoint.x},${currentMousePoint.y}`;
    }
    return points;
  };

  return (
    <svg
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10000
      }}
    >
      {/* Filled polygon area */}
      {polygonPoints.length >= 2 && (
        <polygon
          points={getPolygonPoints()}
          fill="rgba(59, 130, 246, 0.25)"
          stroke="none"
        />
      )}

      {/* Polygon outline */}
      <path
        d={getSvgPath()}
        fill="none"
        stroke="#3b82f6"
        strokeWidth="2"
        strokeDasharray="5,5"
        strokeLinejoin="round"
      />

      {/* Draw points as circles */}
      {polygonPoints.map((point, index) => (
        <g key={index}>
          {/* Point circle */}
          <circle
            cx={point.x}
            cy={point.y}
            r="6"
            fill="#3b82f6"
            stroke="white"
            strokeWidth="2"
          />
          {/* Point number label */}
          <text
            x={point.x}
            y={point.y - 12}
            textAnchor="middle"
            fill="#1e40af"
            fontSize="12"
            fontWeight="bold"
            style={{
              textShadow: '0 0 3px white, 0 0 3px white'
            }}
          >
            {index + 1}
          </text>
        </g>
      ))}

      {/* Current mouse position indicator */}
      {currentMousePoint && polygonPoints.length > 0 && (
        <>
          {/* Line from last point to mouse */}
          <line
            x1={polygonPoints[polygonPoints.length - 1].x}
            y1={polygonPoints[polygonPoints.length - 1].y}
            x2={currentMousePoint.x}
            y2={currentMousePoint.y}
            stroke="#3b82f6"
            strokeWidth="2"
            strokeDasharray="5,5"
          />
          {/* Mouse position circle */}
          <circle
            cx={currentMousePoint.x}
            cy={currentMousePoint.y}
            r="4"
            fill="#93c5fd"
            stroke="white"
            strokeWidth="2"
          />
        </>
      )}

      {/* Closing line preview (back to first point) */}
      {polygonPoints.length >= 2 && currentMousePoint && (
        <line
          x1={currentMousePoint.x}
          y1={currentMousePoint.y}
          x2={polygonPoints[0].x}
          y2={polygonPoints[0].y}
          stroke="#10b981"
          strokeWidth="1"
          strokeDasharray="3,3"
          opacity="0.5"
        />
      )}
    </svg>
  );
};
